/**
 * Shared Parameters Utility
 * Helper functions to consistently add shared reservation parameters to API requests
 */

/**
 * Add shared parameters to API URLs and data objects
 * @param {string|null} baseUrl - Base URL for GET requests (null for POST data only)
 * @param {object|null} dataObject - Data object for POST requests (null for URL only)
 * @param {object} sharedParams - Shared parameters object
 * @param {boolean} sharedParams.isShared - Whether the reservation is shared
 * @param {string} sharedParams.sharedByTenantId - ID of the sharing tenant
 * @returns {object} { url, data } - Modified URL and/or data object
 */
export const addSharedParams = (baseUrl, dataObject, sharedParams) => {
    if (!sharedParams?.isShared || !sharedParams?.sharedByTenantId) {
        return { url: baseUrl, data: dataObject };
    }

    let resultUrl = baseUrl;
    let resultData = dataObject;

    // For URL parameters (GET requests)
    if (typeof baseUrl === 'string') {
        const separator = baseUrl.includes('?') ? '&' : '?';
        resultUrl = `${baseUrl}${separator}is_shared=true&shared_by_tenant_id=${encodeURIComponent(sharedParams.sharedByTenantId)}`;
    }

    // For data objects (POST requests)
    if (dataObject) {
        resultData = {
            ...dataObject,
            is_shared: true,
            shared_by_tenant_id: sharedParams.sharedByTenantId
        };
    }

    return { url: resultUrl, data: resultData };
};

/**
 * Add shared parameters to FormData objects
 * @param {FormData} formData - FormData object to modify
 * @param {object} sharedParams - Shared parameters object
 * @returns {FormData} Modified FormData object
 */
export const addSharedParamsToFormData = (formData, sharedParams) => {
    if (sharedParams?.isShared && sharedParams?.sharedByTenantId) {
        formData.append('is_shared', 'true');
        formData.append('shared_by_tenant_id', sharedParams.sharedByTenantId);
    }
    return formData;
};

/**
 * Build API URL with shared parameters
 * Convenience function for simple URL building
 * @param {string} baseUrl - Base API URL
 * @param {object} sharedParams - Shared parameters object
 * @returns {string} Complete URL with shared parameters
 */
export const buildSharedApiUrl = (baseUrl, sharedParams) => {
    const { url } = addSharedParams(baseUrl, null, sharedParams);
    return url;
};

/**
 * Build data object with shared parameters
 * Convenience function for simple data building
 * @param {object} baseData - Base data object
 * @param {object} sharedParams - Shared parameters object
 * @returns {object} Complete data object with shared parameters
 */
export const buildSharedApiData = (baseData, sharedParams) => {
    const { data } = addSharedParams(null, baseData, sharedParams);
    return data;
};